import {AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors} from "@angular/forms";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {distinctUntilChanged, Observable, of, startWith, switchMap} from "rxjs";
import {ChangeDetectorRef, isSignal, Signal} from "@angular/core";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";

export function leggTilFormArray<T extends FormGroup>(skjema: FormArray<T>, modell: T): FormArray<T> {
    skjema.push(modell);
    return skjema;
}

/** Reaktiverer deaktiverte rader i angitt skjema, kjører angitt arbeid, og deaktivere så radene som ble reaktivert. */
export function medDeaktiverteRaderMidlertidigReaktivert<T extends FormGroup, R>(skjema: FormArray<T>, arbeid: (skjema: FormArray<T>) => R): R {
    const deaktiverteRader = skjema.controls.filter(skjemarad => skjemarad.disabled);
    deaktiverteRader.forEach(skjemarad => skjemarad.enable({emitEvent: false}));
    const resultat = arbeid(skjema);
    deaktiverteRader.forEach(skjemarad => skjemarad.disable({emitEvent: false}));
    return resultat;
}

export function leggTilFeilDersom(erFeil: boolean, nøkkel: string, feltKontroller: FormControl) {
    leggTilFeilMedVerdiDersom(erFeil, nøkkel, true, feltKontroller);
}

export function leggTilFeilMedVerdiDersom(erFeil: boolean, nøkkel: string, verdi: any, feltKontroller: FormControl) {
    if (erFeil) leggTilFeil(feltKontroller, {[nøkkel]: verdi})
    else fjernFeil(feltKontroller, nøkkel);
}

export function leggTilFeil(feltKontroller: FormControl, feil: ValidationErrors) {
    const feilmeldinger = {...feltKontroller.errors, ...feil};
    feltKontroller.setErrors(feilmeldinger);
}

export function fjernFeil(feltKontroller: FormControl, nøkkel: string) {
    const feilmeldinger = {...feltKontroller.errors};
    delete feilmeldinger[nøkkel];
    feltKontroller.setErrors(Object.keys(feilmeldinger).length > 0 ? feilmeldinger : null);
}

export function fjernAlleFeilFraFormGroup(skjema: FormGroup) {
    Object.keys(skjema.controls).forEach((key) => {
        const control = skjema.controls[key] as FormControl;
        control.setErrors(null);
    });
}

export function alleFeil(kontroller: AbstractControl): { [key: string]: ValidationErrors | any } | ValidationErrors[] | null {
    if (kontroller instanceof FormGroup) {
        const feil: { [key: string]: ValidationErrors | any } = {};
        Object.keys(kontroller.controls).forEach(key => {
            const feilIBarn = alleFeil(kontroller.controls[key]);
            if (feilIBarn) feil[key] = feilIBarn;
        });
        return Object.keys(feil).length > 0 ? feil : null;
    }

    if (kontroller instanceof FormArray) {
        const feil: ValidationErrors[] = [];
        kontroller.controls.forEach((childControl, index) => {
            const feilIBarn = alleFeil(childControl);
            if (feilIBarn) feil[index] = feilIBarn;
        });
        return feil.length > 0 ? feil : null;
    }

    if (kontroller instanceof FormControl) return kontroller.errors;

    return null;
}

export function antallKontrollerMedFeil(kontroller: AbstractControl): number {
    if (kontroller instanceof FormGroup || kontroller instanceof FormArray)
        return Object.values(kontroller.controls).map(antallKontrollerMedFeil).reduce((a, b) => a + b, 0);

    if (kontroller instanceof FormControl)
        return erIkkeNil(kontroller.errors) ? 1 : 0;

    return 0;
}


type AktiverFormKontrollerOpsjoner = {
    onlySelf?: boolean;
    emitEvent?: boolean;
}

export function aktiverFormKontroller(aktivert: boolean, kontroller: AbstractControl, opsjoner?: AktiverFormKontrollerOpsjoner) {
    if (aktivert) kontroller.enable(opsjoner);
    else kontroller.disable(opsjoner);
}

export function deaktiverFormKontroller(deaktivert: boolean, kontroller: AbstractControl, opsjoner?: AktiverFormKontrollerOpsjoner) {
    aktiverFormKontroller(!deaktivert, kontroller, opsjoner);
}

/** FormArray.push (og øvrige metoder på FormArray) trigger ikke rendering, trigger derfor her manuelt oppdatering av GUI når antall innslag i angitt FormArray endres */
export function oppdaterGuiVedEndringIFormArrayLengde(formArrayKontroll: Signal<FormArray<any>> | Observable<FormArray<any>> | FormArray<any>, cdRef: ChangeDetectorRef) {
    const formArrayKontrollSomObserverbar = isSignal(formArrayKontroll) ?
      toObservable(formArrayKontroll) :
      (formArrayKontroll instanceof FormArray) ?
        of(formArrayKontroll) :
        formArrayKontroll;

    formArrayKontrollSomObserverbar
      .pipe(
        switchMap(skjema => skjema.valueChanges),
        distinctUntilChanged((a, b) => a === b, samling => samling!.length),
        takeUntilDestroyed())
      .subscribe(() => cdRef.markForCheck());
}

/** Returnerer en observerbar som lever skjemaet verdier umiddelbart samt hver gang et felt i skjemaet endres */
export function skjemaendringerSomObserverbar<T extends AbstractControl>(skjema:Signal<T>): Observable<T['value']> {
    return toObservable(skjema).pipe(
      switchMap(skjema => skjema.valueChanges.pipe(startWith(skjema.value))));
}
